<script setup lang="ts">
  import type { Task } from '@stuut/api/models';
  import { stripHTML } from '@stuut/common/src/utils';
  import { Button } from '@ui/components/button';
  import { HoverCardContent } from '@ui/components/hover-card';

  interface Props {
    canBlankReply?: boolean;
    isApplying: Record<string, boolean>;
    isBlankReplyDisabled?: boolean;
    overflowCount: number;
    totalCount: number;
    visibleSuggestions: Task[];
  }

  withDefaults(defineProps<Props>(), {
    canBlankReply: true,
  });

  const emit = defineEmits<{
    (e: 'use', task: Task): void;
    (e: 'blankReply'): void;
    (e: 'scrollToMore'): void;
  }>();

  const getTaskPreview = (task: Task): string => {
    const html = task.responseData?.data?.html_body ?? '';
    return stripHTML(html).replace(/\s+/g, ' ').trim();
  };
</script>

<template>
  <HoverCardContent
    align="end"
    side="bottom"
    :side-offset="6"
    class="w-96 p-0 overflow-hidden"
    @click.stop
  >
    <div
      class="relative overflow-hidden border-b border-amber-200/40 dark:border-amber-700/30 px-4 py-2.5 bg-amber-50/40 dark:bg-amber-950/20"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute z-0 inset-0 [background:radial-gradient(ellipse_at_top_right,hsla(48,96%,60%,0.18),transparent_70%)]"
      />
      <div class="relative z-10 flex items-center gap-2">
        <i class="fa-solid fa-sparkles text-amber-600 dark:text-amber-300 text-2xs" />
        <span
          class="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-800 dark:text-amber-200"
        >
          {{
            totalCount > 1
              ? `Draft using reply from task · ${totalCount}`
              : 'Draft using reply from task'
          }}
        </span>
      </div>
    </div>

    <div class="flex flex-col divide-y divide-border/40">
      <div v-for="tt in visibleSuggestions" :key="tt.id" class="px-4 py-3 flex flex-col gap-y-2">
        <div class="flex items-start gap-2 text-sm font-semibold tracking-tight">
          {{ tt.label }}
        </div>

        <p
          v-if="getTaskPreview(tt)"
          class="text-xs text-muted-foreground font-light line-clamp-2 leading-relaxed"
        >
          {{ getTaskPreview(tt) }}
        </p>

        <p v-else class="text-2xs text-muted-foreground/60">Loading preview...</p>

        <Button
          size="sm"
          variant="default"
          :loading="isApplying[tt.id]"
          class="gap-x-1.5"
          @click.stop.prevent="emit('use', tt)"
        >
          <i class="fa-solid fa-sparkles text-2xs" />
          <span>Draft with task reply</span>
        </Button>
      </div>

      <button
        v-if="overflowCount > 0"
        type="button"
        class="px-4 py-2 text-[11px] uppercase tracking-[0.15em] font-semibold text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors flex items-center justify-between"
        @click.stop.prevent="emit('scrollToMore')"
      >
        <span>+ {{ overflowCount }} more suggestion{{ overflowCount > 1 ? 's' : '' }}</span>
        <i class="fa fa-arrow-down text-2xs" />
      </button>
    </div>

    <div
      v-if="canBlankReply"
      class="border-t border-border bg-secondary px-4 py-2 flex items-center justify-between"
    >
      <span class="text-2xs uppercase tracking-widest text-muted-foreground font-medium">
        Or compose your own
      </span>

      <Button
        size="xs"
        variant="transparent"
        :disabled="isBlankReplyDisabled"
        @click.stop.prevent="emit('blankReply')"
      >
        <i class="fa far fa-reply text-2xs!" />
        <span>Blank reply</span>
      </Button>
    </div>
  </HoverCardContent>
</template>
